<template>
  <footer class="mf-footer">
    <div class="mf-top-accent"></div>
    <div class="mf-content">
      <div class="mf-grid">
        <!-- Site Links -->
        <div class="mf-section">
          <div class="mf-section-header">
            <div class="mf-section-icon">
              <v-icon color="white" size="16">mdi-link-variant</v-icon>
            </div>
            <h6 class="mf-section-title">Site Links</h6>
          </div>
          <div class="mf-link-grid">
            <a
              href="https://certifai.readthedocs.io/en/latest/"
              class="mf-link"
              target="_blank"
              rel="noopener"
            >
              <v-icon x-small class="mf-link-icon">mdi-file-document-outline</v-icon>
              Documentation
            </a>
            <a
              href="https://gitlab.com/certifai/documentation/-/issues/"
              class="mf-link"
              target="_blank"
              rel="noopener"
            >
              <v-icon x-small class="mf-link-icon">mdi-bug-outline</v-icon>
              Report an issue
            </a>
            <a href="/privacy-page" class="mf-link">
              <v-icon x-small class="mf-link-icon">mdi-shield-lock-outline</v-icon>
              Privacy Policy
            </a>
            <a href="/cookies-page" class="mf-link">
              <v-icon x-small class="mf-link-icon">mdi-cookie-outline</v-icon>
              Cookies Policy
            </a>
          </div>
        </div>

        <!-- Project Info -->
        <div class="mf-section mf-section--center">
          <div class="mf-section-header">
            <div class="mf-section-icon mf-section-icon--info">
              <v-icon color="white" size="16">mdi-information-outline</v-icon>
            </div>
            <h6 class="mf-section-title">Project Information</h6>
          </div>
          <div class="mf-project-badge">
            HORIZON-CL3-2022-CS-01-04
          </div>
          <p class="mf-project-subtitle">Increased Cybersecurity 2022</p>
          <p class="mf-project-ref">
            <v-icon x-small color="rgba(255,255,255,0.5)" class="mr-1">mdi-calendar</v-icon>
            Ref. Ares(2022)8966412 — 23/12/2022
          </p>
        </div>

        <!-- EU Co-funding -->
        <div class="mf-section mf-section--end">
          <div class="mf-eu-badge">
            <img
              src="https://cybersuiteproject.eu/wp-content/uploads/2024/03/EN_Co-fundedbytheEU_RGB_NEG.png"
              alt="Co-Funded by the EU"
              class="mf-eu-logo"
            />
          </div>
          <p class="mf-disclaimer">
            Co-Funded by the European Union. Views and opinions expressed are those of the author(s)
            only and do not necessarily reflect those of the European Union or Digital Europe
            Programme. Neither the European Union nor Digital Europe Programme can be held
            responsible for them.
          </p>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="mf-bottom-bar">
        <div class="mf-copyright">
          <v-icon x-small color="rgba(255,255,255,0.5)" class="mr-1">mdi-shield-check</v-icon>
          CertifAI Platform &copy; {{ new Date().getFullYear() }}
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import imageEcFlag from '@/assets/ec_flag.png';

export default defineComponent({
  name: 'MinimalFooter',
  props: {
    showBasicLinks: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return { imageEcFlag };
  },
});
</script>

<style scoped>
/* ========================================
   Minimal Footer - Modern UI/UX
   ======================================== */

.mf-footer {
  position: relative;
  width: 100%;
}

/* Gradient accent line at top */
.mf-top-accent {
  height: 3px;
  background: linear-gradient(90deg, #1a237e, #3949ab, #42a5f5, #66bb6a, #ffa726, #ef5350);
  background-size: 200% 100%;
  animation: accentShimmer 8s ease infinite;
}

@keyframes accentShimmer {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.mf-content {
  background: linear-gradient(135deg, #0d1321 0%, #1a1f36 40%, #1b2a4a 100%);
  padding: 32px 48px 0;
}

/* Grid Layout */
.mf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 32px;
  padding-bottom: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Sections */
.mf-section {
  display: flex;
  flex-direction: column;
}
.mf-section--center {
  align-items: center;
  text-align: center;
}
.mf-section--end {
  align-items: flex-end;
  text-align: right;
}

.mf-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}

.mf-section-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1565c0, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.mf-section-icon--info {
  background: linear-gradient(135deg, #7b1fa2, #ab47bc);
}

.mf-section-title {
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin: 0;
}

/* Links - 2x2 Grid */
.mf-link-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 8px;
}

.mf-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  font-size: 0.82rem;
  padding: 5px 8px;
  border-radius: 8px;
  transition: all 0.25s ease;
  position: relative;
  white-space: nowrap;
}
.mf-link:hover {
  color: white;
  background: rgba(66, 165, 245, 0.12);
  transform: translateX(4px);
}

.mf-link-icon {
  color: rgba(255, 255, 255, 0.4) !important;
  transition: color 0.25s ease !important;
}
.mf-link:hover .mf-link-icon {
  color: #42a5f5 !important;
}

/* Project Badge */
.mf-project-badge {
  display: inline-block;
  background: linear-gradient(135deg, rgba(26, 35, 126, 0.5), rgba(57, 73, 171, 0.4));
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 8px 18px;
  border-radius: 10px;
  margin-bottom: 10px;
}

.mf-project-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0 0 8px;
}

.mf-project-ref {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* EU Section */
.mf-eu-badge {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 20px;
  margin-bottom: 14px;
  transition: background 0.3s ease;
}
.mf-eu-badge:hover {
  background: rgba(255, 255, 255, 0.08);
}

.mf-eu-logo {
  width: 180px;
  height: auto;
  opacity: 0.9;
  transition: opacity 0.3s ease;
}
.mf-eu-badge:hover .mf-eu-logo {
  opacity: 1;
}

.mf-disclaimer {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.72rem;
  line-height: 1.5;
  max-width: 320px;
  margin: 0;
  text-align: right;
}

/* Bottom Bar */
.mf-bottom-bar {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 16px 0;
  display: flex;
  justify-content: center;
}

.mf-copyright {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

/* Responsive */
@media (max-width: 960px) {
  .mf-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .mf-content {
    padding: 24px 20px 0;
  }
  .mf-section--center {
    align-items: flex-start;
    text-align: left;
  }
  .mf-section--end {
    align-items: flex-start;
    text-align: left;
  }
  .mf-disclaimer {
    text-align: left;
  }
  .mf-project-ref {
    justify-content: flex-start;
  }
  .mf-link-grid {
    grid-template-columns: 1fr;
  }
}

/* Override Vuetify defaults */
.v-application .mf-footer p {
  margin-bottom: 0;
}
</style>
